import { Dispatch, SetStateAction } from 'react';
import moment from 'moment-timezone';
import { Pencil, Trash2, ExternalLink } from 'lucide-react';
import { fDateInBrandTZ } from '../../../utils/formatTime';
import { brandConfig } from '../../../config';
import {
  ReservationGeneral,
  ReservationGuestType
} from '../../../models/owner-portal/reservationGeneral';
import { DetailContainer } from '../../../layouts/simple/DetailColumnAndRow';
import { fCurrency } from '../../../utils/formatNumber';
import { Button } from '../../../components/ui/Button';
import { guestPortalOrigin } from '../../../utils/owner-portal/url/url';
import { useOptionalReservationOwnerDialogsContext } from '../../tables/reservation/ReservationOwnerDialogsContext';

/** Moment format matching former date-fns `M/dd/yyyy` (US numeric). */
const US_NUMERIC_DATE = 'M/DD/YYYY';

// ----------------------------------------------------------------------
export type confirmActionT = 'edit' | 'cancel' | 'edit_confirm';
type Props = {
  reservation: ReservationGeneral | null;
  handleOpenCancelConfirmed: VoidFunction;
  setOpenEditDatePicker: Dispatch<SetStateAction<boolean>>;
};
// ----------------------------------------------------------------------

export default function ReservationDetails({
  reservation,
  handleOpenCancelConfirmed,
  setOpenEditDatePicker
}: Props) {
  const ownerDialogs = useOptionalReservationOwnerDialogsContext();

  if (!reservation) {
    return null;
  }

  const tz = brandConfig.timezone;
  const brandTodayStart = moment().tz(tz).startOf('day');
  const checkInBrandStart = moment(reservation.check_in_ISO).tz(tz).startOf('day');
  const checkOutBrandStart = moment(reservation.check_out_ISO).tz(tz).startOf('day');

  return (
    <DetailContainer className="gap-4 h-full">
      <div className="flex-1 overflow-auto space-y-4">
      {/* Header: property name + booking ref */}
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Reservation Details
        </h1>
      </header>

      {/* Property + ref + link: outside grid; only add margin when link is present so we don't leave a gap when it's hidden */}
      <div
        className="space-y-1"
      >
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {reservation.name_detailed}
        </dt>
        <dd className="text-sm font-medium tabular-nums text-foreground">{reservation.name}</dd>
        {reservation.guest_type === ReservationGuestType.Owner && (
          <dd className="pb-1">
            <a
              href={`${guestPortalOrigin(window.location.hostname)}/reservation/${encodeURIComponent(reservation.name)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              View in Guest Portal
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
          </dd>
        )}
      </div>

      {/* Details grid: two columns on sm+, single column on mobile */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
        {reservation.guest_name && (
          <div className="space-y-1">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Guest
            </dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {reservation.guest_name}
            </dd>
          </div>
        )}
        <div className="space-y-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Type
          </dt>
          <dd className="text-sm font-medium tabular-nums text-foreground">{reservation.type}</dd>
        </div>

        <div className="space-y-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Check In
          </dt>
          <dd className="text-sm font-medium tabular-nums text-foreground">
            {fDateInBrandTZ(reservation.check_in_ISO, US_NUMERIC_DATE)}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Check Out
          </dt>
          <dd className="text-sm font-medium tabular-nums text-foreground">
            {fDateInBrandTZ(reservation.check_out_ISO, US_NUMERIC_DATE)}
          </dd>
        </div>

        {reservation.guest_type !== ReservationGuestType.Guest && (
          <div className="space-y-1">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Nights
            </dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {reservation.nights}
            </dd>
          </div>
        )}
        {reservation.guest_type === ReservationGuestType.Guest ? (
          <div className="space-y-1 sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Booked On
            </dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {fDateInBrandTZ(reservation.booking_date_ISO, US_NUMERIC_DATE)}
            </dd>
          </div>
        ) : (
          <div className="space-y-1">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Booked On
            </dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {fDateInBrandTZ(reservation.booking_date_ISO, US_NUMERIC_DATE)}
            </dd>
          </div>
        )}
      </dl>

      {/* Pricing section (Guest bookings only) – same layout as timeshare details ListingPriceCard */}
      {reservation.guest_type === ReservationGuestType.Guest &&
        (reservation.rental_amount != null || reservation.nightly_rate != null) && (
          <section
            className="rounded-lg border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-4"
            aria-labelledby="reservation-pricing-heading"
          >
            <h2 id="reservation-pricing-heading" className="sr-only">
              Pricing
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-y-1 sm:items-baseline">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Nightly Rate
                </p>
                <p className="text-2xl font-bold leading-tight tabular-nums text-foreground">
                  {reservation.nightly_rate != null ? fCurrency(reservation.nightly_rate) : '–'}
                </p>
              </div>
              <div className="space-y-1 sm:text-right">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Rental Amount
                </p>
                <p className="text-2xl font-bold tabular-nums leading-tight">
                  {reservation.rental_amount != null ? fCurrency(reservation.rental_amount) : '–'}
                </p>
                <p className="text-sm text-muted-foreground leading-tight">
                  {reservation.nights} night{reservation.nights === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          </section>
        )}

      {reservation.special_requests_detail && (
        <section className="space-y-1">
          <h2 className="sr-only">Additional details</h2>
          <dl className="space-y-1">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Special Request
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {reservation.special_requests_detail}
              </dd>
            </div>
          </dl>
        </section>
      )}
      {typeof reservation.owner_pays_guest_clean === 'boolean' &&
        reservation.no_departure_clean &&
        (reservation.guest_type === ReservationGuestType.Guest ||
          reservation.guest_type === ReservationGuestType.OwnerGuest) && (
          <section className="space-y-1">
            <dl className="space-y-1">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Guest paying final cleaning
                </dt>
                <dd className="text-sm font-medium text-foreground capitalize">
                  {reservation.owner_pays_guest_clean ? 'No' : 'Yes'}
                </dd>
              </div>
            </dl>
          </section>
        )}
      </div>

      {(reservation.guest_type === ReservationGuestType.Owner ||
        reservation.guest_type === ReservationGuestType.OwnerGuest) && (
        <section className="flex flex-col gap-3 sm:max-w-sm mt-auto pt-4">
          <Button
            onClick={() => {
              if (ownerDialogs) {
                ownerDialogs.handleOpenConfirm('edit');
              } else {
                setOpenEditDatePicker(true);
              }
            }}
            disabled={checkOutBrandStart.isBefore(brandTodayStart)}
            className="w-full"
          >
            <Pencil className="mr-2 h-4 w-4" />
            Change Dates
          </Button>
          <Button
            onClick={() => {
              if (ownerDialogs) {
                ownerDialogs.handleOpenCancelConfirmed();
              } else {
                handleOpenCancelConfirmed();
              }
            }}
            disabled={checkInBrandStart.isBefore(brandTodayStart)}
            className="w-full"
            variant="destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Cancel Booking
          </Button>
        </section>
      )}
    </DetailContainer>
  );
}
